import { useState, useEffect } from 'react';
import { Calendar, Edit3, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabase';
import { formatDateString } from '../lib/utils';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '../components/ui/dialog';

// shadcn/ui components
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Helper function to convert 24-hour time to 12-hour AM/PM format
function convertToAmPm(time24: string): string {
  if (!time24) return '';
  const [hourStr, minuteStr] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minuteStr} ${period}`;
}

interface AgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
}

interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string;
  location_address: string;
  instructions?: string;
  special_notes?: string;
  age_groups?: AgeGroup[];
  evaluation_session_age_groups?: { age_group_id: string; age_groups: AgeGroup }[];
}

interface SessionModalProps {
  session: EvaluationSession | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { ageGroupIds: string[]; sessionDate: string; startTime: string; endTime: string; locationName: string; locationAddress: string; instructions: string; specialNotes: string }) => void;
  onDelete?: (sessionId: string) => void;
  ageGroups: AgeGroup[];
}

function SessionModal({ isOpen, onClose, onSave, onDelete, session, ageGroups }: SessionModalProps) {
  const [selectedAgeGroupIds, setSelectedAgeGroupIds] = useState<string[]>([]);
  const [sessionDate, setSessionDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [locationAddress, setLocationAddress] = useState<string>('');
  const [instructions, setInstructions] = useState<string>('');
  const [specialNotes, setSpecialNotes] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  useEffect(() => {
    if (session) {
      // Extract age group IDs from the session
      const ageGroupIds = session.evaluation_session_age_groups
        ? session.evaluation_session_age_groups.map(ag => ag.age_group_id)
        : [];
      
      setSelectedAgeGroupIds(ageGroupIds);
      setSessionDate(session.session_date);
      setStartTime(session.start_time);
      setEndTime(session.end_time || '');
      setLocationName(session.location_name || '');
      setLocationAddress(session.location_address || '');
      setInstructions(session.instructions || '');
      setSpecialNotes(session.special_notes || '');
    } else {
      setSelectedAgeGroupIds([]);
      setSessionDate('');
      setStartTime('');
      setEndTime('');
      setLocationName('');
      setLocationAddress('');
      setInstructions('');
      setSpecialNotes('');
    }
  }, [session]);

  const handleAgeGroupChange = (ageGroupId: string) => {
    setSelectedAgeGroupIds(prev => {
      // If already selected, remove it
      if (prev.includes(ageGroupId)) {
        return prev.filter(id => id !== ageGroupId);
      }
      // Otherwise add it
      return [...prev, ageGroupId];
    });
  };

  const handleSave = () => {
    if (selectedAgeGroupIds.length === 0 || !sessionDate || !startTime || !endTime) {
      setFormError('At least one age group, session date, start time, and end time are required.');
      return;
    }
    setFormError('');
    onSave({ ageGroupIds: selectedAgeGroupIds, sessionDate, startTime, endTime, locationName, locationAddress, instructions, specialNotes });
  };

  const handleDelete = () => {
    if (session && onDelete) {
      if (window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
        onDelete(session.id);
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{session ? 'Edit Evaluation Session' : 'Add Evaluation Session'}</DialogTitle>
          <DialogDescription>Configure session details below.</DialogDescription>
        </DialogHeader>
        {formError && (
          <div className="mb-4 rounded-md bg-red-50 p-2">
            <p className="text-sm text-red-600">{formError}</p>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session Date</label>
            <Input
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time (24-hour)</label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time (24-hour)</label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
            <Input
              type="text"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter session instructions"
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Notes</label>
            <Input
              type="text"
              value={specialNotes}
              onChange={(e) => setSpecialNotes(e.target.value)}
              placeholder="Enter any special notes"
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
            <Input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="Enter location name"
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Address</label>
            <Input
              type="text"
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              placeholder="Enter location address"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age Groups</label>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
              {[...ageGroups].sort((a, b) => {
                // First sort by sex (female first, then male)
                if (a.sex !== b.sex) {
                  return a.sex === 'female' ? -1 : 1;
                }
                // Then sort by group_name
                return a.group_name.localeCompare(b.group_name);
              }).map((group) => (
                <div key={group.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`age-group-${group.id}`}
                    checked={selectedAgeGroupIds.includes(group.id)}
                    onChange={() => handleAgeGroupChange(group.id)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`age-group-${group.id}`} className="ml-2 block text-sm text-gray-900">
                    {group.group_name} {group.sex === 'male' ? 'Boys' : 'Girls'}
                  </label>
                </div>
              ))}
            </div>
            {selectedAgeGroupIds.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select at least one age group</p>
            )}
          </div>
          <DialogFooter className="flex justify-between w-full">
            <div>
              {session && (
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleDelete}
                  className="flex items-center"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button variant="secondary">Cancel</Button>
              </DialogClose>
              <Button type="submit">Save</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type SortField = 'ageGroup' | 'date' | 'startTime' | 'endTime' | 'location';
type SortDirection = 'asc' | 'desc';

export function EvaluationSessions() {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [sessions, setSessions] = useState<EvaluationSession[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<EvaluationSession | null>(null);
  const [error, setError] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  async function fetchAgeGroups() {
    try {
      const { data, error } = await supabase
        .from('age_groups')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching age groups:', error);
        setError(error.message);
      } else {
        setAgeGroups(data);
      }
    } catch (err) {
      console.error('Unexpected error fetching age groups:', err);
      setError('Unexpected error fetching age groups.');
    }
  }

  async function fetchSessions() {
    setLoadingSessions(true);
    try {
      const { data, error } = await supabase
        .from('evaluation_sessions')
        .select(`
          *,
          evaluation_session_age_groups!inner(
            age_group_id,
            age_groups(id, group_name, sex)
          )
        `)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching evaluation sessions:', error);
        setError(error.message);
      } else {
        // Process the data to group by evaluation session
        const processedSessions = data.reduce((acc: EvaluationSession[], session) => {
          const existingSession = acc.find(s => s.id === session.id);
          
          if (existingSession) {
            // Add this age group to the existing session
            if (session.evaluation_session_age_groups) {
              existingSession.evaluation_session_age_groups = [
                ...(existingSession.evaluation_session_age_groups || []),
                ...session.evaluation_session_age_groups
              ];
            }
          } else {
            // Add as a new session
            acc.push(session);
          }
          
          return acc;
        }, []);
        
        setSessions(processedSessions);
      }
    } catch (err) {
      console.error('Unexpected error fetching sessions:', err);
      setError('Unexpected error fetching sessions.');
    }
    setLoadingSessions(false);
  }

  useEffect(() => {
    fetchAgeGroups();
    fetchSessions();
  }, []);

  const handleOpenAddModal = () => {
    setSelectedSession(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (session: EvaluationSession) => {
    setSelectedSession(session);
    setIsModalOpen(true);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedSessions = () => {
    return [...sessions].sort((a, b) => {
      // Apply appropriate sort logic based on the field
      let comparison = 0;
      
      switch (sortField) {
        case 'ageGroup':
          // Sort by the first age group name
          const aAgeGroup = a.evaluation_session_age_groups && a.evaluation_session_age_groups[0]?.age_groups?.group_name || '';
          const bAgeGroup = b.evaluation_session_age_groups && b.evaluation_session_age_groups[0]?.age_groups?.group_name || '';
          comparison = aAgeGroup.localeCompare(bAgeGroup);
          break;
          
        case 'date':
          // Sort by date
          comparison = a.session_date.localeCompare(b.session_date);
          break;
          
        case 'startTime':
          // Sort by start time
          comparison = a.start_time.localeCompare(b.start_time);
          break;
          
        case 'endTime':
          // Sort by end time
          comparison = a.end_time.localeCompare(b.end_time);
          break;
          
        case 'location':
          // Sort by location name
          const aLocation = a.location_name || '';
          const bLocation = b.location_name || '';
          comparison = aLocation.localeCompare(bLocation);
          break;
      }
      
      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  async function handleDeleteSession(sessionId: string) {
    try {
      // 1. Delete all associated age group entries first
      const { error: deleteAgeGroupsError } = await supabase
        .from('evaluation_session_age_groups')
        .delete()
        .eq('evaluation_session_id', sessionId);
      
      if (deleteAgeGroupsError) throw deleteAgeGroupsError;
      
      // 2. Delete the session itself
      const { error: deleteSessionError } = await supabase
        .from('evaluation_sessions')
        .delete()
        .eq('id', sessionId);
      
      if (deleteSessionError) throw deleteSessionError;
      
      setIsModalOpen(false);
      fetchSessions();
    } catch (err: unknown) {
      console.error('Error deleting session:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete session.');
    }
  }

  async function handleSaveSession(data: { ageGroupIds: string[]; sessionDate: string; startTime: string; endTime: string; locationName: string; locationAddress: string; instructions: string; specialNotes: string }) {
    try {
      if (selectedSession) {
        // Edit existing session
        // 1. Update the session details
        const { error: sessionUpdateError } = await supabase
          .from('evaluation_sessions')
          .update({
            session_date: data.sessionDate,
            start_time: data.startTime,
            end_time: data.endTime,
            location_name: data.locationName,
            location_address: data.locationAddress,
            instructions: data.instructions,
            special_notes: data.specialNotes
          })
          .eq('id', selectedSession.id);
        if (sessionUpdateError) throw sessionUpdateError;
        
        // 2. Delete all existing age group associations
        const { error: deleteError } = await supabase
          .from('evaluation_session_age_groups')
          .delete()
          .eq('evaluation_session_id', selectedSession.id);
        if (deleteError) throw deleteError;
        
        // 3. Insert new age group associations
        const ageGroupEntries = data.ageGroupIds.map(ageGroupId => ({
          evaluation_session_id: selectedSession.id,
          age_group_id: ageGroupId
        }));
        
        const { error: insertError } = await supabase
          .from('evaluation_session_age_groups')
          .insert(ageGroupEntries);
        if (insertError) throw insertError;
      } else {
        // Add new session
        // 1. Insert the new session
        const { data: newSession, error: sessionError } = await supabase
          .from('evaluation_sessions')
          .insert({
            session_date: data.sessionDate,
            start_time: data.startTime,
            end_time: data.endTime,
            location_name: data.locationName,
            location_address: data.locationAddress,
            instructions: data.instructions,
            special_notes: data.specialNotes
          })
          .select();
        if (sessionError) throw sessionError;
        
        if (!newSession || newSession.length === 0) {
          throw new Error('Failed to create new session');
        }
        
        // 2. Insert age group associations
        const ageGroupEntries = data.ageGroupIds.map(ageGroupId => ({
          evaluation_session_id: newSession[0].id,
          age_group_id: ageGroupId
        }));
        
        const { error: insertError } = await supabase
          .from('evaluation_session_age_groups')
          .insert(ageGroupEntries);
        if (insertError) throw insertError;
      }
      
      setIsModalOpen(false);
      fetchSessions();
    } catch (err: unknown) {
      console.error('Error saving session:', err);
      setError(err instanceof Error ? err.message : 'Failed to save session.');
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center mb-6">
          <Calendar className="w-8 h-8 text-indigo-600 mr-2" />
          <h3 className="text-2xl font-semibold text-gray-800">Evaluation Sessions</h3>
        </div>
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-600 rounded">
            {error}
          </div>
        )}
        <div className="mb-4">
          <Button onClick={handleOpenAddModal}>Add Evaluation Session</Button>
        </div>
        <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100" 
                  onClick={() => handleSort('ageGroup')}
                >
                  <div className="flex items-center">
                    Age Group
                    {sortField === 'ageGroup' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100" 
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center">
                    Session Date
                    {sortField === 'date' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100" 
                  onClick={() => handleSort('startTime')}
                >
                  <div className="flex items-center">
                    Start Time
                    {sortField === 'startTime' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100" 
                  onClick={() => handleSort('endTime')}
                >
                  <div className="flex items-center">
                    End Time
                    {sortField === 'endTime' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100" 
                  onClick={() => handleSort('location')}
                >
                  <div className="flex items-center">
                    Location
                    {sortField === 'location' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    )}
                  </div>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingSessions ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : sessions.length > 0 ? (
                getSortedSessions().map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      {session.evaluation_session_age_groups && session.evaluation_session_age_groups.length > 0
                        ? session.evaluation_session_age_groups.map(ag => 
                            ag.age_groups ? `${ag.age_groups.group_name} ${ag.age_groups.sex === 'male' ? 'Boys' : 'Girls'}` : ''
                          ).join(', ')
                        : 'No Age Groups'}
                    </TableCell>
                    <TableCell>{formatDateString(session.session_date, 'en-US', 'short')}</TableCell>
                    <TableCell>{convertToAmPm(session.start_time)}</TableCell>
                    <TableCell>{convertToAmPm(session.end_time)}</TableCell>
                    <TableCell>
                      {session.location_name ? (
                        <span title={session.location_address || ''}>{session.location_name}</span>
                      ) : (
                        <span className="text-gray-400">Not specified</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditModal(session)}
                        title="Edit Session"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No evaluation sessions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-6">
          <Link to="/administration" className="text-indigo-600 hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      </main>
      <SessionModal
        session={selectedSession}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSession}
        onDelete={handleDeleteSession}
        ageGroups={ageGroups}
      />
    </div>
  );
}